/**
 * placement-snapshot.ts — the Ausschreibungs-Screen Tab 1 (Risikoprofil) + Tab 2
 * (Soll-Deckungskatalog) contract (P2 Placement Slice 5).
 *
 * Two placement children live behind these types:
 *   - `placement_snapshot` — the frozen Kunde/Police copy at tender time (Ch 2.3). The GENERAL
 *     snapshot is a read-only copy of the Kunde Risikoprofil; the BRANCH snapshot carries the
 *     per-object Police Ist-Attribute, EDITABLE for this placement with a frozen `origin_attributes`.
 *   - `placement_step2_target_coverage_answers` — one answer row per resolved Police Soll rule
 *     (Kunde Basissparten ride along as a hint strip, Q27).
 *
 * This module is the SINGLE SOURCE OF TRUTH for the snapshot_json v1 shape, the wire shapes the
 * two tabs read, the two write bodies, the status/consent gating constants and the pure helpers
 * (kritisch/offen detection, progress, effective target, changed-keys diff, prune, freeze). The
 * api owns every decision (materialization, gating, E12); the web only renders + triggers.
 *
 * [Source: docs/product/p2-slices/slice-5-snapshot-soll-deckung-design.md §1/§3]
 * [Source: packages/shared/src/placement.ts] (triad + wire-type idioms)
 */

import type {
  RisikoprofilFeldBasis,
  RisikoprofilKategorie,
  RisikoprofilTyp,
  GesundheitspruefungStatus,
  EinkommensBandbreite,
  RisikoprofilStammdatenMirror,
  BasisspartenStatus,
  BasissparteStatusOverride,
  BasisspartenAmpel,
  KundeSchadenQuelle,
} from './kunde-risikoprofil';
import type { SollPflichtStatus, PoliceRisikoSource } from './police-risiko';
import type { PlacementStatus, PlacementClosureStatus } from './placement';
import type { PlacementSchadenverlaufRef } from './policy-schadenverlauf';

// ── Snapshot type + schema version ──────────────────────────────────────────────

/** The two snapshot rows per placement (one per snapshot_type; 0146 CHECK). */
export const PLACEMENT_SNAPSHOT_TYPE = {
  GENERAL: 'risk_profile_general',
  BRANCH: 'risk_profile_branch',
} as const;
export type PlacementSnapshotType =
  (typeof PLACEMENT_SNAPSHOT_TYPE)[keyof typeof PLACEMENT_SNAPSHOT_TYPE];

/** The snapshot_json schema version frozen into every row (bump = a migration of the json shape). */
export const PLACEMENT_SNAPSHOT_SCHEMA_VERSION = 1 as const;

// ── Step-2 answer status (offen → beantwortet → bestaetigt) ─────────────────────

export const STEP2_ANSWER_STATUS = {
  OFFEN: 'offen',
  BEANTWORTET: 'beantwortet',
  BESTAETIGT: 'bestaetigt',
} as const;
export type Step2AnswerStatus = (typeof STEP2_ANSWER_STATUS)[keyof typeof STEP2_ANSWER_STATUS];

export const STEP2_ANSWER_STATUS_VALUES: readonly Step2AnswerStatus[] = [
  'offen',
  'beantwortet',
  'bestaetigt',
];

export const STEP2_ANSWER_STATUS_LABELS: Record<Step2AnswerStatus, string> = {
  offen: 'Offen',
  beantwortet: 'Beantwortet',
  bestaetigt: 'Bestaetigt',
};

/** Where a Step-2 answer's prefill came from (Q21: the Referenzpolice's policy-level Soll). */
export const STEP2_PREFILL_SOURCE = {
  NONE: 'none',
  POLICE: 'police',
} as const;
export type Step2PrefillSource = (typeof STEP2_PREFILL_SOURCE)[keyof typeof STEP2_PREFILL_SOURCE];

// ── Editability + Kritisch definition ───────────────────────────────────────────

/**
 * A snapshot (branch Ist + Step-2 answers) is only editable while the placement is in one of these
 * statuses AND the closure is still `offen`. From `ausgeschrieben` on the tab is read-only (Ch 9).
 */
export const PLACEMENT_SNAPSHOT_EDITABLE_STATUSES: readonly PlacementStatus[] = [
  'entwurf',
  'risikoprofil_erfasst',
  'vorgaben_erfasst',
];

/** Kritische Pflichtdeckung = a rule whose Pflicht-Status is `pflicht` (Q35; not bedingt_pflicht). */
export const PLACEMENT_KRITISCH_PFLICHT_STATUS: readonly SollPflichtStatus[] = ['pflicht'];

// ── Domain events ────────────────────────────────────────────────────────────────

/** The in-process domain-event names (carried on the activity row; no Kafka). */
export const PLACEMENT_SNAPSHOT_EVENTS = {
  SNAPSHOT_CREATED: 'placement.snapshot_created',
  SNAPSHOT_REFRESHED: 'placement.snapshot_refreshed',
  RISK_PROFILE_COMPLETED: 'placement.risk_profile_completed',
  STEP2_COMPLETED: 'placement.step2_target_coverage_completed',
  PFLICHTDECKUNG_UNBESTAETIGT: 'placement.pflichtdeckung_unbestaetigt',
  PFLICHTDECKUNG_KENNTNISNAHME: 'placement.pflichtdeckung_kenntnisnahme',
} as const;
export type PlacementSnapshotEvent =
  (typeof PLACEMENT_SNAPSHOT_EVENTS)[keyof typeof PLACEMENT_SNAPSHOT_EVENTS];

/** The Hinweisbox shown atop both tabs (Ch 7.3 + the Promotion note). No em dashes; umlauts direct. */
export const PLACEMENT_HINWEISBOX_TEXT =
  'Diese Daten sind ein Snapshot zum Zeitpunkt der Ausschreibung. Aenderungen am Risikoprofil ' +
  'oder an der Soll-Deckung wirken nur innerhalb dieser Ausschreibung und beruehren die ' +
  'Kunden- bzw. Policendaten nicht. Erst beim Kundenentscheid werden die uebernommenen Werte ' +
  'auf die Police zurueckgeschrieben.';

// ── snapshot_json v1 shapes ──────────────────────────────────────────────────────

/** One frozen branch Ist-field inside a snapshot (the code/schema field shape + pflicht). */
export interface SnapshotFeld extends RisikoprofilFeldBasis {
  pflicht: boolean;
}

/** One frozen Kunde Risikoprofil field (adds the card grouping). */
export interface SnapshotKundeFeld extends SnapshotFeld {
  kategorie: RisikoprofilKategorie;
}

/** One frozen Basissparte (the hint-strip row; carries the derived Ampel + the catalog id). */
export interface SnapshotBasissparte {
  sparte_key: string;
  branchencode: string;
  label: string;
  default_status: BasisspartenStatus;
  kriterium: string;
  richtwert: string;
  applicable: boolean | null;
  status_override: BasissparteStatusOverride | null;
  ampel: BasisspartenAmpel;
  rule_id: string | null;
}

/**
 * One frozen Schadenverlauf-je-Sparte row (P2 hardening Q15). Only STORED rows are frozen into
 * the snapshot (the live Schadenauskunft hints are not). Additive to snapshot json v1.
 */
export interface SnapshotSchadenSparte {
  branchencode: string;
  label: string;
  anzahl_schaeden: number;
  art_schaeden: string | null;
  schadensumme: number | null;
  zeitraum_von: string;
  zeitraum_bis: string;
  quelle: KundeSchadenQuelle;
}

/** The GENERAL snapshot json (the frozen Kunde Risikoprofil copy). Gated keys only when kept. */
export interface PlacementSnapshotGeneralJson {
  version: typeof PLACEMENT_SNAPSHOT_SCHEMA_VERSION;
  profile_type: RisikoprofilTyp;
  attributes: Record<string, unknown>;
  felder: SnapshotKundeFeld[];
  /** Present only when NOT suppressed at materialization (consent + role). */
  gesundheitspruefung_status?: GesundheitspruefungStatus | null;
  einkommens_bandbreite?: EinkommensBandbreite | null;
  data_quality_score: number | null;
  last_reviewed_at: string | null;
  stammdaten_mirror: RisikoprofilStammdatenMirror | null;
  basissparten: SnapshotBasissparte[];
  /**
   * Schadenverlauf je Sparte (P2 hardening Q15) — the frozen STORED rows. Optional + additive:
   * a snapshot created before the change simply lacks the key (the web shows a "nicht im Snapshot"
   * note); schema version stays 1.
   */
  schaeden_je_sparte?: SnapshotSchadenSparte[];
  profile_updated_at: string;
}

/** One insured-object Ist set inside the BRANCH snapshot (origin = frozen prefill, attributes = working). */
export interface PlacementSnapshotObjekt {
  insurance_object_ref: string | null;
  object_label: string | null;
  /** The frozen prefill copied at materialization (never mutated after). */
  origin_attributes: Record<string, unknown>;
  /** The editable working copy for this placement. */
  attributes: Record<string, unknown>;
  origin_source: PoliceRisikoSource | null;
  origin_policy_risk_attributes_id: string | null;
  updated_at: string;
}

/** The BRANCH snapshot json (the editable Police Ist copy). `felder` are frozen into the json. */
export interface PlacementSnapshotBranchJson {
  version: typeof PLACEMENT_SNAPSHOT_SCHEMA_VERSION;
  branchencode: string;
  felder: SnapshotFeld[];
  objekte: PlacementSnapshotObjekt[];
}

// ── Wire shapes (snake_case) ──────────────────────────────────────────────────────

/** The GENERAL snapshot wire = the json + the row envelope + the re-gate/consent flags + counters. */
export interface PlacementGeneralSnapshotWire extends PlacementSnapshotGeneralJson {
  snapshot_id: string;
  created_at: string;
  updated_at: string;
  /** Gated keys suppressed when the row was MATERIALIZED (frozen on the row). */
  consent_gated_fields_suppressed: string[];
  /** Gated keys additionally suppressed on THIS read (role hidden and/or consent now revoked). */
  gated_fields_suppressed_on_read: string[];
  consent_present_at_snapshot: boolean;
  consent_present_now: boolean;
  filled: number;
  total: number;
}

/** One branch-snapshot object on the wire (adds filled/total + the changed-keys diff). */
export interface PlacementSnapshotObjektWire extends PlacementSnapshotObjekt {
  filled: number;
  total: number;
  /** Keys whose working value differs from the frozen prefill ("Abweichend zur Police"). */
  changed_keys: string[];
}

/** The BRANCH snapshot wire. */
export interface PlacementBranchSnapshotWire {
  version: typeof PLACEMENT_SNAPSHOT_SCHEMA_VERSION;
  branchencode: string;
  felder: SnapshotFeld[];
  snapshot_id: string;
  created_at: string;
  updated_at: string;
  objekte: PlacementSnapshotObjektWire[];
}

/** GET /placements/:id/risikoprofil response (Tab 1). */
export interface PlacementRisikoprofilResponse {
  placement_id: string;
  placement_number: string;
  status: PlacementStatus;
  editable: boolean;
  policy_id: string | null;
  branchencode: string;
  /** The placement's insurance_object_ref scope (null = policy-level / all objects). */
  scoped_object_ref: string | null;
  /** The frozen Kunde snapshot; null when the caller lacks `customer_risk.read` (reason `permission`). */
  general: PlacementGeneralSnapshotWire | null;
  general_hidden_reason: 'permission' | null;
  /** The branch Ist snapshot (policy-risk copy); null when a policy-backed placement's caller lacks `policy_risk.read`. */
  branch: PlacementBranchSnapshotWire | null;
  branch_hidden_reason: 'permission' | null;
  /** Schadenverlauf is a read-only ref (Slice 6; Ch 7.3) — the Referenzpolice's current entry or a reason. */
  schadenverlauf: PlacementSchadenverlaufRef;
}

/** PATCH /placements/:id/risikoprofil/branch body (merge-patch of one object's working attributes). */
export interface PlacementBranchIstPatchBody {
  insurance_object_ref?: string | null;
  object_label?: string | null;
  attributes: Record<string, unknown>;
  expected_updated_at?: string;
}

// ── Pflichtdeckung (E12) ──────────────────────────────────────────────────────────

/** The E12 Pflichtdeckung-Warnflag state on the placement + the live unbestaetigt-kritisch list. */
export interface PflichtdeckungWire {
  warnflag: boolean;
  warn_at: string | null;
  warn_rule_keys: string[];
  ack_at: string | null;
  ack_by: string | null;
  /** The rule_keys of kritische (pflicht) rules not yet `bestaetigt` — computed per read. */
  unbestaetigt_kritisch: string[];
}

// ── Step-2 (Soll-Deckungskatalog) wire ─────────────────────────────────────────────

/** One Step-2 answer row (a resolved Police Soll rule merged with the stored override). */
export interface Step2AnswerItem {
  id: string;
  rule_key: string;
  rule_id: string | null;
  branchencode: string;
  label: string;
  pflicht_status: SollPflichtStatus;
  rechtsgrundlage: string | null;
  ziel_text: string;
  catalog_target_value: number | null;
  prefill_source: Step2PrefillSource;
  target_value_override: number | null;
  selbstbehalt_override: number | null;
  prioritaet_override: number | null;
  freitext_begruendung: string | null;
  /** COALESCE(target_value_override, catalog_target_value). */
  effective_target_value: number | null;
  /** #8: a police-prefilled value masked for a reader without `policy_risk.read` (override nulled, catalog shown). */
  prefill_hidden: boolean;
  status: Step2AnswerStatus;
  is_kritisch: boolean;
  answered_by: string | null;
  answered_by_name: string | null;
  answered_at: string | null;
  updated_at: string;
}

/** GET /placements/:id/soll-deckung response (Tab 2). */
export interface PlacementSollDeckungResponse {
  placement_id: string;
  status: PlacementStatus;
  editable: boolean;
  branchencode: string;
  items: Step2AnswerItem[];
  /** The Kunde Basissparten relevant to this branch (Q27 hint strip, not separate answer rows). */
  basissparten_hinweis: SnapshotBasissparte[];
  progress: { beantwortet: number; total: number };
  pflichtdeckung: PflichtdeckungWire;
  /** #8: set when police-prefilled values were masked for a reader without `policy_risk.read`. */
  police_prefill_hidden_reason: 'permission' | null;
}

/** PUT /placements/:id/soll-deckung/:ruleKey body (true merge; explicit null clears). */
export interface Step2AnswerPutBody {
  target_value_override?: number | null;
  selbstbehalt_override?: number | null;
  prioritaet_override?: number | null;
  freitext_begruendung?: string | null;
  status?: Step2AnswerStatus;
  expected_updated_at?: string;
}

// ── Pure helpers ──────────────────────────────────────────────────────────────────

/** A snapshot is editable while the status is in the editable set AND the closure is still offen. */
export function isPlacementSnapshotEditable(
  status: PlacementStatus,
  closureStatus: PlacementClosureStatus,
): boolean {
  return (
    (PLACEMENT_SNAPSHOT_EDITABLE_STATUSES as readonly string[]).includes(status) &&
    closureStatus === 'offen'
  );
}

/** Is this Pflicht-Status a kritische Pflichtdeckung (Q35 = `pflicht` only)? */
export function isKritischePflichtdeckung(pflichtStatus: SollPflichtStatus): boolean {
  return (PLACEMENT_KRITISCH_PFLICHT_STATUS as readonly string[]).includes(pflichtStatus);
}

/** The rule_keys of kritische (pflicht) answers NOT yet `bestaetigt` (the E12 trigger set). */
export function kritischUnbestaetigt(
  items: readonly Pick<Step2AnswerItem, 'rule_key' | 'is_kritisch' | 'status'>[],
): string[] {
  return items
    .filter((it) => it.is_kritisch && it.status !== STEP2_ANSWER_STATUS.BESTAETIGT)
    .map((it) => it.rule_key);
}

/** The rule_keys of non-`optional` answers still `offen` (the Tab-2 abschliessen gate). */
export function offeneNichtOptionale(
  items: readonly Pick<Step2AnswerItem, 'rule_key' | 'pflicht_status' | 'status'>[],
): string[] {
  return items
    .filter((it) => it.pflicht_status !== 'optional' && it.status === STEP2_ANSWER_STATUS.OFFEN)
    .map((it) => it.rule_key);
}

/** The Tab-2 progress counters: answered (status <> offen) out of total. */
export function step2Progress(
  items: readonly Pick<Step2AnswerItem, 'status'>[],
): { beantwortet: number; total: number } {
  let beantwortet = 0;
  for (const it of items) if (it.status !== STEP2_ANSWER_STATUS.OFFEN) beantwortet += 1;
  return { beantwortet, total: items.length };
}

/** The effective Zielsumme: the broker override if set, else the catalog default. */
export function effectiveTargetValue(
  override: number | null,
  catalog: number | null,
): number | null {
  return override ?? catalog;
}

/**
 * The keys whose working value differs from the frozen origin ("Abweichend zur Police"). A key
 * present in exactly one side, or present on both with a differing JSON serialization, is changed.
 */
export function snapshotChangedKeys(
  origin: Record<string, unknown>,
  working: Record<string, unknown>,
): string[] {
  const keys = new Set([...Object.keys(origin), ...Object.keys(working)]);
  const changed: string[] = [];
  for (const k of keys) {
    const inOrigin = Object.prototype.hasOwnProperty.call(origin, k);
    const inWorking = Object.prototype.hasOwnProperty.call(working, k);
    if (inOrigin !== inWorking) {
      changed.push(k);
    } else if (JSON.stringify(origin[k]) !== JSON.stringify(working[k])) {
      changed.push(k);
    }
  }
  return changed.sort();
}

/** Keep only the attribute keys that exist in the given frozen `felder` (prune off-catalog keys). */
export function pruneToFelder(
  attrs: Record<string, unknown>,
  felder: readonly { key: string }[],
): Record<string, unknown> {
  const allow = new Set(felder.map((f) => f.key));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(attrs)) {
    if (allow.has(k)) out[k] = v;
  }
  return out;
}

/** Freeze a resolved field definition into the minimal snapshot field shape (drops schema internals). */
export function freezeFeld(def: RisikoprofilFeldBasis & { pflicht?: boolean }): SnapshotFeld {
  const frozen: SnapshotFeld = {
    key: def.key,
    label: def.label,
    typ: def.typ,
    pflicht: def.pflicht ?? false,
  };
  if (def.options) frozen.options = [...def.options];
  if (def.einheit !== undefined) frozen.einheit = def.einheit;
  if (def.hinweis !== undefined) frozen.hinweis = def.hinweis;
  return frozen;
}
